import { SeloDeMaquina } from '../../../components/SeloDeMaquina'
import { SeveridadeDaIa } from '../../../components/SeveridadeDaIa'
import { TEXTOS } from '../../../lib/glossario'
import type { AlertaDaIa, ParecerDaIa } from '../../../types/chamado'

/**
 * A leitura da IA sobre o chamado, como ela aparece no cartão da fiscalização.
 *
 * **Não é mais um cartão à parte, e a mudança é a razão de este arquivo existir.** Enquanto o
 * parecer morava num cartão abaixo do formulário, conferir uma resposta custava rolar a tela,
 * achar o trecho correspondente e voltar — e o custo caía justamente sobre quem a IA deveria
 * ajudar. Agora a leitura e as perguntas do julgamento estão no mesmo cartão, com a sugestão
 * colada em cada pergunta.
 *
 * O limite não mudou por causa disso, e o texto da tela repete: a IA sugere e explica; quem
 * responde, salva e assina é a fiscalização (Regra nº 3).
 */

export function LeituraDoParecer({ parecer }: { parecer: ParecerDaIa }) {
  if (parecer.situacao === 'FALHOU') {
    return (
      <p className="text-sm text-slate-600">
        {TEXTOS.analiseIa.falhou}
        {parecer.erro ? `: ${parecer.erro}` : '.'}
      </p>
    )
  }
  return (
    <div className="space-y-3">
      {parecer.resumo && <p className="text-sm leading-relaxed text-slate-700">{parecer.resumo}</p>}

      {parecer.alertas.length === 0 ? (
        <p className="text-sm text-slate-500">{TEXTOS.analiseIa.semAlertas}</p>
      ) : (
        <div>
          <h3 className="text-sm font-medium text-slate-700">{TEXTOS.analiseIa.alertas}</h3>
          <ul className="mt-2 space-y-2">
            {parecer.alertas.map((alerta, indice) => (
              <Alerta key={`${alerta.codigo}-${indice}`} alerta={alerta} />
            ))}
          </ul>
        </div>
      )}

      {/* A procedência do parecer, sempre visível: qual modelo, quando, e o que ele não é. A
          frase mora num componente só — três telas mostram texto de máquina, e três cópias da
          mesma ressalva viram três ressalvas diferentes. */}
      <SeloDeMaquina modelo={parecer.modelo} quando={parecer.concluidoEm} />
    </div>
  )
}

function Alerta({ alerta }: { alerta: AlertaDaIa }) {
  return (
    <li className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <SeveridadeDaIa severidade={alerta.severidade} />
        <span className="text-sm font-medium text-slate-800">{alerta.titulo ?? alerta.codigo}</span>
      </div>
      {alerta.explicacao && <p className="mt-1 text-sm text-slate-600">{alerta.explicacao}</p>}
      {alerta.verificacao && (
        <p className="mt-1 text-sm text-slate-500">
          <span className="font-medium">{TEXTOS.analiseIa.comoConferir}</span> {alerta.verificacao}
        </p>
      )}
    </li>
  )
}

